import { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import Btn from '../../components/Btn/Btn';
import dayjs from 'dayjs';
import i18next from 'i18next';  // Import the i18next instance
import Select from 'react-select';
import RequiredFormLabel from '../RequiredFormLabel/RequiredFormLabel';
import CardPlaceholder from '../CardPlaceholder/CardPlaceholder';
import MissingAccountRecordTypeAlert from '../MissingAccountRecordTypeAlert/MissingAccountRecordTypeAlert';
import { Col, Form, Row } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { Formik } from 'formik';
import ValidationError from '../ValidationError/ValidationError';
import { useApiClient } from '../../hooks/useApiClient';
import { FORMAT_DATE_TIME } from '../../utils/constants';
import { AccountTypeOption, Markets, ReactSelectOption } from '../../types';
import { RoleEnum } from '../../api';
import { RecordType } from '../../api/models/AccountEntity';
import { toast } from 'react-toastify';
import { GTMsendEvent } from '../../utils/trackingGA4';
import { useTranslation } from 'react-i18next';
import { translateSfAccountsTypes } from '../../utils/translations';
import { RecordTypeEnum } from '../../api/models/Enums'
import AccountCreateRequestDTO from '../../api/models/Accounts/AccountCreateRequestDTO'
import useLeadsStore from '../../store/LeadsStore';
import { useErrorToast } from 'src/hooks/useErrorToast';

export type AccountFormInitValues = AccountCreateRequestDTO & { CreatedDate: string; Id?: string; }

const isInvalidFlottaField = (value: any) => {
	if (_.isNil(value)) return true;
	if (_.isString(value) && _.isEmpty(value)) return true;
	return false;
}

const AccountForm = ({
	accountId,
	mode,
	initialValues,
	RecordType,
	enableDuplicateModal = false,
	afterSubmitFn,
	namePartner,
	pickPersistent = false,
}: {
	accountId?: string;
	mode: 'edit' | 'create' | 'readonly';
	initialValues: AccountFormInitValues;
	RecordType?: RecordType;
	enableDuplicateModal?: boolean;
	afterSubmitFn?: (accountId: string) => void;
	namePartner?: string;
	pickPersistent?: boolean;
}) => {
	const { apiClient, handleApi } = useApiClient();
	const { userRole, user } = useAuth();
	const [loading, setLoading] = useState(true);
	const [accountType, setAccountType] = useState<AccountTypeOption | null>(null);
	const [error, setError] = useState(null);
	const [accountTypes, setAccountTypes] = useState<AccountTypeOption[]>([]);
	const [partners, setPartners] = useState<ReactSelectOption[]>([])
	const [partner, setPartner] = useState<ReactSelectOption | null>(null)
	const { t } = useTranslation()
	const { activeLead } = useLeadsStore()
	const { showError } = useErrorToast()

	const validateFn = (values: AccountFormInitValues) => {
		const errors: any = {};
		const REQUIRED_MESSAGE = i18next.t('mandatory');

		if (_.isEmpty(values.RecordTypeId)) {
			errors.RecordTypeId = REQUIRED_MESSAGE;
		} else {
			const recordType = getRecordTypeFromId(values.RecordTypeId)!;

			if (!values.Codice_Fiscale__c || values.Codice_Fiscale__c === '') {
				errors.Codice_Fiscale__c = REQUIRED_MESSAGE
			}

			if (recordType.value !== RecordTypeEnum.Privato.toString() && values.Partita_IVA__c === '') {
				errors.Partita_IVA__c = REQUIRED_MESSAGE
			}

			if (_.isEmpty(values.reference_partner) && _.isEmpty(partner)) {
				errors.reference_partner = REQUIRED_MESSAGE
			}

			if (userRole !== RoleEnum.SEGNALATORE) {
				if (_.isEmpty(values.BillingCity)) {
					errors.BillingCity = REQUIRED_MESSAGE
				}

				if (_.isEmpty(values.BillingStreet)) {
					errors.BillingStreet = REQUIRED_MESSAGE
				}
			}

			if (recordType.value === RecordTypeEnum.Azienda.toString()) {
				if (values.Name === '') {
					errors.Name = REQUIRED_MESSAGE;
				}

				if (userRole !== RoleEnum.SEGNALATORE) {
					if (isInvalidFlottaField(values.Flotta_Potenziale_PC__c)) {
						errors.Flotta_Potenziale_PC__c = REQUIRED_MESSAGE
					}
					if (isInvalidFlottaField(values.Flotta_Potenziale_LCV__c)) {
						errors.Flotta_Potenziale_LCV__c = REQUIRED_MESSAGE
					}
					if (isInvalidFlottaField(values.Flotta_Totale_PC__c)) {
						errors.Flotta_Totale_PC__c = REQUIRED_MESSAGE
					}
					if (isInvalidFlottaField(values.Flotta_Totale_LCV__c)) {
						errors.Flotta_Totale_LCV__c = REQUIRED_MESSAGE
					}
				}
			} else {
				if (values.FirstName === '') {
					errors.FirstName = REQUIRED_MESSAGE;
				}
				if (values.LastName === '') {
					errors.LastName = REQUIRED_MESSAGE;
				}
			}

			if (_.isEmpty(values.Email__c)) {
				errors.Email__c = REQUIRED_MESSAGE;
			}

			if (_.isEmpty(values.Phone)) {
				errors.Phone = REQUIRED_MESSAGE;
			}
		}
		return errors
	};

	const getRecordTypeFromId = (id: string) => {
		const recordType = accountTypes.find(el => el.value === id);
		return recordType
	}

	const getPivaCfDuplicates = async ({ cf, piva }: { cf?: string; piva?: string }) => {
		const promises: any = [];

		if (!!cf) promises.push(apiClient.salesforceAccountController.getAccounts({ cf }));
		if (!!piva) promises.push(apiClient.salesforceAccountController.getAccounts({ piva }))

		const [byCf, byPiva] = await Promise.all(promises);

		const found = (byCf?.data || []).concat(byPiva?.data || []);
		const unique = _.uniqBy(found, (item) => item.Id);
		console.debug('duplicate accounts', unique);
		return unique;
	}

	const submitFn = async (values: AccountFormInitValues) => {

		if (mode === 'readonly') {
			return;
		}

		// Request to backend
		let requestBody: any = {
			Codice_Fiscale__c: null,
			Partita_IVA__c: null,
			RecordTypeId: values.RecordTypeId,
			Email__c: values.Email__c,
			Phone: values.Phone,
			reference_partner: partner?.value || values.reference_partner,
			BillingCity: values.BillingCity,
			BillingStreet: values.BillingStreet,
			Flotta_Potenziale_PC__c: values.Flotta_Potenziale_PC__c ?? undefined,
			Flotta_Potenziale_LCV__c: values.Flotta_Potenziale_LCV__c ?? undefined,
			Flotta_Totale_PC__c: values.Flotta_Totale_PC__c ?? undefined,
			Flotta_Totale_LCV__c: values.Flotta_Totale_LCV__c ?? undefined,
		}

		const recordType = getRecordTypeFromId(values.RecordTypeId);

		if (!recordType) {
			// Error out
			return
		}

		if (recordType.value === RecordTypeEnum.Privato.toString()) {
			requestBody.Codice_Fiscale__c = values.Codice_Fiscale__c
		} else {
			requestBody.Partita_IVA__c = values.Partita_IVA__c
			requestBody.Codice_Fiscale__c = values.Codice_Fiscale__c
		}

		if (recordType.value === RecordTypeEnum.Azienda.toString()) {
			requestBody.Name = values.Name
		} else {
			requestBody.FirstName = values.FirstName
			requestBody.LastName = values.LastName
		}

		let accountIdRefresh = ''
		try {
			if (mode === 'edit' && accountId) {
				accountIdRefresh = accountId;
				await handleApi(apiClient.salesforceAccountController.editAccount(accountId, requestBody));
				toast.success(i18next.t('account_edited_success'))
			} else if (mode === 'create') {
				GTMsendEvent({
					event: 'complete_new_account',
					category: 'account',
					action: 'complete',
					label: 'new_account'
				})

				const persistentAccounts = await getPivaCfDuplicates({
					cf: requestBody.Codice_Fiscale__c,
					piva: requestBody.Partita_IVA__c
				});

				if (!pickPersistent && persistentAccounts?.length > 0) {
					toast.error(i18next.t('existing_account_same_p_iva'));
					return;
				}

				if (pickPersistent && persistentAccounts?.length > 1) {
					toast.error(i18next.t('existing_account_same_p_iva'));
					return;
				}

				if (pickPersistent
					&& persistentAccounts?.length === 1
					&& recordType.value === persistentAccounts?.[0]?.RecordType?.Id
				) {
					accountIdRefresh = persistentAccounts?.[0].Id
				} else {
					let resp = await handleApi(apiClient.salesforceAccountController.createAccount(requestBody))
					accountIdRefresh = resp.id;
					toast.success(i18next.t('account_created_success'))
				}
			}
		} catch (err: any) {
			let errMsg = err?.body?.messageList ? err?.body?.messageList[0] : '';
			if (errMsg?.includes('duplicated_account') || errMsg?.includes('valore duplicato')) {
				toast.error(i18next.t('existing_account_same_p_iva'))
			} else if (errMsg.endsWith('was not found on Saleforce')) {
				toast.error(_.capitalize(errMsg))
			} else {
				showError(err, i18next.t('cannot_create_modify_account'))
			}

			return
		}

		if (afterSubmitFn) {
			afterSubmitFn(accountIdRefresh)
		}
	}

	useEffect(() => {
		if (!RecordType) {
			Promise.all([
				handleApi(apiClient.salesforceAccountController.getAccountTypes()),
				handleApi(apiClient.userController.getRegisterSingleUser(user.username)),
				handleApi(apiClient.salesforceAccountController.getAccountFields())
			])
				.then((res) => {
					const [accRes, userRes, accountFields] = res;

					let accTyps = accRes.data.map(el => {
						return { label: translateSfAccountsTypes(el.Name), value: el.Id }
					});

					if (['edit', 'readonly', 'create'].includes(mode)) {
						const accTyp = accTyps.find(el => el.value === initialValues.RecordTypeId)! || null;
						setAccountType(accTyp)
					}

					setAccountTypes(accTyps);

					// OLD whenreference_partner was filled with dealers
					// let partners: ReactSelectOption[] = dealerRes.map(el => {
					//     return { label: el.ragione_sociale, value: el.id }
					// })

					// partners = partners.filter(el => {
					//     return userRes.reference_partner.includes(el.value)
					// })

					let partners = accountFields[0]?.picklistValues.filter(el => {
						return userRes.reference_partner.includes(el.value)
					})

					setPartners(partners)
					setPartner(partners.find(el => el.label === initialValues.reference_partner) || null)
				})
				.catch((err) => {
					setError(err);
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			//@ts-ignore
			setAccountType({ label: RecordType.Name, value: RecordType.Id })
			setLoading(false);
		}
	}, [])

	const partnerOptions = useMemo(() => {
		const currentValue = initialValues?.reference_partner
		if (!currentValue) {
			return partners || [];
		}

		const found = (partners || []).find(partner => partner.value === currentValue)
		if (found) return partners;

		if (![RoleEnum.BACKOFFICE, RoleEnum.MASTER, RoleEnum.AREA_MANAGER].includes(userRole)) {
			return partners
		}

		return [...(partners || []), { value: currentValue, label: currentValue }];
	}, [initialValues?.reference_partner, partners, userRole])

	return (
		<div>
			{loading ? (
				<CardPlaceholder />
			) : (
				<Formik
					initialValues={initialValues}
					validate={validateFn}
					onSubmit={submitFn}
				>
					{
						({ errors, values, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
							<Form autoComplete='off' onSubmit={handleSubmit}>
								<Row>
									{initialValues.Id && !accountType && (
										<MissingAccountRecordTypeAlert accountId={initialValues.Id} />
									)}
									<Col md={6}>
										<Form.Group className="mb-3">
											<RequiredFormLabel label={i18next.t('type')} required={mode !== 'readonly'} />
											<Select
												menuPosition="fixed"
												name="RecordTypeId"
												isDisabled={['edit', 'readonly'].includes(mode)}
												placeholder={i18next.t('select_type')}
												options={accountTypes}
												defaultValue={accountType}
												onChange={(opt: any) => {
													setAccountType(opt)
													setFieldValue('RecordTypeId', opt.value)
													setFieldValue('Codice_Fiscale__c', '')
													setFieldValue('Partita_IVA__c', '')
													if (opt.value !== RecordTypeEnum.Azienda) {
														setFieldValue('Flotta_Potenziale_LCV__c', null)
														setFieldValue('Flotta_Potenziale_PC__c', null)
														setFieldValue('Flotta_Totale_LCV__c', null)
														setFieldValue('Flotta_Totale_PC__c', null)
													}
												}}
											/>
											<ValidationError {...{ errors, touched, name: 'RecordTypeId' }} />
										</Form.Group>
									</Col >
								</Row >
								<hr />

								{
									accountType !== null && (
										<div>
											<Row>
												{accountType?.value === RecordTypeEnum.Azienda.toString() ? (
													<Col md={6}>
														<Form.Group className="mb-3">
															<RequiredFormLabel
																label={_.capitalize(i18next.t('business_name')?.toLowerCase())}
																required={mode !== 'readonly'}
															/>
															<Form.Control type="text"
																name='Name'
																required
																disabled={['readonly'].includes(mode)}
																onChange={(e) => {
																	handleChange(e)
																}}
																defaultValue={'dsfs'}
																value={values.Name}
															/>
															<ValidationError {...{ errors, touched, name: 'Name' }} />
														</Form.Group>
													</Col>
												) : (
													<>
														<Col md={6}>
															<Form.Group className="mb-3">
																<RequiredFormLabel label={i18next.t('name')} required={mode !== 'readonly'} />
																<Form.Control type="text"
																	name='FirstName'
																	required
																	disabled={['readonly'].includes(mode)}
																	onChange={(e) => {
																		handleChange(e)
																	}}
																	value={values.FirstName}
																/>
																<ValidationError {...{ errors, touched, name: 'FirstName' }} />
															</Form.Group>
														</Col>
														<Col md={6}>
															<Form.Group className="mb-3">
																<RequiredFormLabel label={i18next.t('surname')} required={mode !== 'readonly'} />
																<Form.Control type="text"
																	name='LastName'
																	required
																	disabled={['readonly'].includes(mode)}
																	onChange={(e) => {
																		handleChange(e)
																	}}
																	value={values.LastName}
																/>
																<ValidationError {...{ errors, touched, name: 'LastName' }} />
															</Form.Group>
														</Col>
													</>
												)}
											</Row>
											<hr />
											<Row>
												{accountType?.value === RecordTypeEnum.Privato.toString() ? (
													<Col md={6}>
														<Form.Group className="mb-3">
															<RequiredFormLabel label={i18next.t('fiscal_code')} required={mode !== 'readonly'} />
															<Form.Control type="text"
																disabled={['readonly'].includes(mode) || (mode === 'edit' && [RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole))}
																onChange={(e) => {
																	handleChange(e)
																}}
																name='Codice_Fiscale__c'
																required
																value={values.Codice_Fiscale__c || ''}
															/>
														</Form.Group>
													</Col>
												) : (
													<>
														<Col md={6}>
															<Form.Group className="mb-3">
																<RequiredFormLabel label={i18next.t('business_fiscal_code')} required={mode !== 'readonly'} />
																<Form.Control type="text"
																	name='Partita_IVA__c'
																	disabled={['readonly'].includes(mode) || (mode === 'edit' && [RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole))}
																	required
																	onChange={(e) => {
																		handleChange(e)
																	}}
																	value={values.Partita_IVA__c || ''}
																/>
															</Form.Group>
														</Col>
														<Col md={6}>
															<Form.Group className="mb-3">
																<RequiredFormLabel label={i18next.t('fiscal_code')} required={mode !== 'readonly'} />
																<Form.Control type="text"
																	disabled={!!values.Codice_Fiscale__c
																		? ['readonly'].includes(mode) || (mode === 'edit' && [RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole))
																		: false
																	}
																	onChange={(e) => {
																		handleChange(e)
																	}}
																	name='Codice_Fiscale__c'
																	required
																	value={values.Codice_Fiscale__c || ''}
																/>
															</Form.Group>
														</Col>
													</>
												)}
											</Row>
											<hr />
											<Row>
												<Col md={6}>
													<Form.Group className="mb-3">
														<RequiredFormLabel label={t('phone')} />
														<Form.Control type="text"
															name='Phone'
															disabled={['readonly'].includes(mode)}
															onChange={(e) => {
																handleChange(e)
															}}
															value={values.Phone}
														/>
														<ValidationError {...{ errors, touched, name: 'Phone' }} />
													</Form.Group>
												</Col>
												<Col md={6}>
													<Form.Group className="mb-3">
														<RequiredFormLabel label={t('email')} />
														<Form.Control type="email"
															name='Email__c'
															disabled={['readonly'].includes(mode)}
															onChange={(e) => {
																handleChange(e)
															}}
															autoComplete="off"
															value={values.Email__c}
														/>
														<ValidationError {...{ errors, touched, name: 'Email__c' }} />
													</Form.Group>
												</Col>
											</Row>
											<div className='mt-4'></div>
											{userRole !== RoleEnum.SEGNALATORE && (
												<>
													<hr />
													<div className='mt-4'></div>
													<Row>
														<Col md={6}>
															<Form.Group className="mb-3">
																<RequiredFormLabel label={i18next.t('BillingCity')} />
																<Form.Control type="text"
																	name='BillingCity'
																	disabled={['readonly'].includes(mode)}
																	onChange={handleChange}
																	value={values.BillingCity || ''}
																/>
																<ValidationError {...{ errors, touched, name: 'BillingCity' }} />
															</Form.Group>
														</Col>
														<Col md={6}>
															<Form.Group className="mb-3">
																<RequiredFormLabel label={i18next.t('BillingStreet')} />
																<Form.Control type="text"
																	name='BillingStreet'
																	disabled={['readonly'].includes(mode)}
																	onChange={handleChange}
																	value={values.BillingStreet || ''}
																/>
																<ValidationError {...{ errors, touched, name: 'BillingStreet' }} />
															</Form.Group>
														</Col>
													</Row>
												</>
											)}
											{accountType?.value === RecordTypeEnum.Azienda.toString() && userRole !== RoleEnum.SEGNALATORE && (<>
												<hr />
												<div className='mt-4'></div>
												<Row>
													<Col md={6}>
														<Form.Group className="mb-3">
															<RequiredFormLabel label={i18next.t('Flotta_Potenziale_PC__c')} />
															<Form.Control
																type="number"
																step="0.1"
																name='Flotta_Potenziale_PC__c'
																disabled={['readonly'].includes(mode)}
																onChange={handleChange}
																value={values.Flotta_Potenziale_PC__c ?? undefined}
															/>
															<ValidationError {...{ errors, touched, name: 'Flotta_Potenziale_PC__c' }} />
														</Form.Group>
													</Col>
													<Col md={6}>
														<Form.Group className="mb-3">
															<RequiredFormLabel label={i18next.t('Flotta_Potenziale_LCV__c')} />
															<Form.Control
																type="number"
																step="0.1"
																name='Flotta_Potenziale_LCV__c'
																disabled={['readonly'].includes(mode)}
																onChange={handleChange}
																value={values.Flotta_Potenziale_LCV__c ?? undefined}
															/>
															<ValidationError {...{ errors, touched, name: 'Flotta_Potenziale_LCV__c' }} />
														</Form.Group>
													</Col>
												</Row>
												<Row>
													<Col md={6}>
														<Form.Group className="mb-3">
															<RequiredFormLabel label={i18next.t('Flotta_Totale_PC__c')} />
															<Form.Control
																type="number"
																step="0.1"
																name='Flotta_Totale_PC__c'
																disabled={['readonly'].includes(mode)}
																onChange={handleChange}
																value={values.Flotta_Totale_PC__c ?? undefined}
															/>
															<ValidationError {...{ errors, touched, name: 'Flotta_Totale_PC__c' }} />
														</Form.Group>
													</Col>
													<Col md={6}>
														<Form.Group className="mb-3">
															<RequiredFormLabel label={i18next.t('Flotta_Totale_LCV__c')} />
															<Form.Control
																type="number"
																step="0.1"
																name='Flotta_Totale_LCV__c'
																disabled={['readonly'].includes(mode)}
																onChange={handleChange}
																value={values.Flotta_Totale_LCV__c ?? undefined}
															/>
															<ValidationError {...{ errors, touched, name: 'Flotta_Totale_LCV__c' }} />
														</Form.Group>
													</Col>
												</Row>
											</>)}
											<hr />
											<div className='mt-4'></div>
											<Row>
												<Col md={6}>
													<Form.Group className="mb-3">
														<RequiredFormLabel label='Dealer' required={mode !== 'readonly'} />
														<Select
															menuPosition="fixed"
															name="reference_partner"
															isDisabled={['readonly'].includes(mode)}
															placeholder={namePartner ? namePartner : i18next.t('select_partner')}
															options={partnerOptions}
															defaultValue={partner}
															onChange={(opt: any) => {
																setPartner(opt)
															}}
														/>
														<ValidationError {...{ errors, touched, name: 'reference_partner' }} />
													</Form.Group>
												</Col>
												{mode !== 'create' ? (
													<Col md={3}>
														<Form.Group className="mb-3">
															<Form.Label>{t('creation_date')}</Form.Label>
															<Form.Control type="text"
																value={dayjs(values.CreatedDate).format(FORMAT_DATE_TIME)}
																disabled
															/>
														</Form.Group>
													</Col>
												) : null}
											</Row>
											{!!initialValues?.owner?.Name && (
												<Row>
													<Col md={6}>
														<div>{t('account_owner')}: {initialValues?.owner?.Name}</div>
														{/* <div>{t('owner_branch')}: {initialValues?.owner?.Filiale__c}</div> */}
													</Col>
												</Row>)
											}
											{
												mode !== 'readonly' && (
													<Btn
														type="submit"
														icon="arrow-right"
														className="float-end"
														disabled={loading || isSubmitting}
													>
														{mode === 'edit' ? i18next.t('update') : i18next.t('create')}
													</Btn>
												)
											}
										</div>
									)
								}
							</Form >
						)}
				</Formik >
			)}
		</div >
	);
};

export default AccountForm;
